import { inject, Injectable } from '@angular/core';
import { DashboardProfileContextService } from '@rma/dashboard/authentication/public/user';
import { SubscriptionFeatureService } from '@rma/dashboard/billing/subscription-plans/public/api-subscription-features';
import { ContextTrixelsService, TrixelsMetrics } from '@rma/dashboard/generic/api/trixels';
import { SyndicationChannel } from '@rma/dashboard/reporting/public/ui-syndication-channels';
import { REPORTING_TIME_PERIOD } from '@rma/dashboard/reporting/public/util-reporting-time-period';
import type { ExternalReviewSourceType } from '@rma/dashboard/reviews/public/api-external-reviews';
import { ContextReviewsConversionsService, type ConvertedReviewInsights } from '@rma/dashboard/reviews/public/api-reviews-pangea';
import { SocialAnalyticsApiService } from '@rma/dashboard/social-media-manager/public/api-activity-statistics';
import { SocialMediaChannel2 } from '@rma/dashboard/social-media-manager/public/domain';
import { catchAndLogError } from '@rma/generic/rxjs/operators/catch-and-log-error';
import { nonNullable } from '@rma/generic/rxjs/operators/non-nullable';
import { rmaShareReplay } from '@rma/generic/rxjs/operators/rma-share-replay';
import { RmaIcons } from '@rma/generic/ui/icon';
import { catchError, combineLatest, forkJoin, map, Observable, of, switchMap, take } from 'rxjs';
import type { PlatformReviewInfo } from './platform-review-info.model';

@Injectable()
export class SyndicationStatisticsService {
  private readonly contextReviewsConversionsService = inject(ContextReviewsConversionsService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly socialAnalyticsApiService = inject(SocialAnalyticsApiService);
  private readonly subscriptionFeatureService = inject(SubscriptionFeatureService);
  private readonly timePeriod = inject(REPORTING_TIME_PERIOD);
  private readonly trixelsService = inject(ContextTrixelsService);

  public readonly externalPlatformsWithReviews$: Observable<SyndicationChannel[]>;
  public readonly externalReviewPlatform$: Observable<ExternalReviewSourceType | null>;
  public readonly googleReviewInsights$: Observable<ConvertedReviewInsights>;
  public readonly platformIcon$: Observable<RmaIcons>;
  public readonly realtorDomainExposures$: Observable<PlatformReviewInfo | null>;

  public readonly rmaExposures$: Observable<number>;

  public constructor() {
    const { from, to } = this.timePeriod;
    const socialChannelsWithPostedReviews$ = this.getSocialChannelsWithReviews(from, to);
    const widgetExposures$ = this.getIntegrationTrixels(from, to, undefined, 'Widgets');

    this.rmaExposures$ = this.getIntegrationTrixels(from, to, 'RateMyAgent');
    this.externalReviewPlatform$ = this.getExternalReviewPlatform();
    this.googleReviewInsights$ = this.contextReviewsConversionsService.getConvertedReviewInsights(from).pipe(rmaShareReplay());

    this.realtorDomainExposures$ = this.externalReviewPlatform$.pipe(
      switchMap((platform) => (platform ? this.getIntegrationInsights(platform, from, to) : of(null))),
      rmaShareReplay(),
    );

    this.platformIcon$ = this.externalReviewPlatform$.pipe(
      nonNullable(),
      // SAFETY(legacy): unreviewed, pre-dates this rule.
      map((platform) => `platform-${platform.toLowerCase()}` as RmaIcons),
    );

    this.externalPlatformsWithReviews$ = forkJoin([
      this.googleReviewInsights$.pipe(
        catchError(() => of(null)),
        take(1),
      ),
      this.externalReviewPlatform$.pipe(
        catchError(() => of(null)),
        take(1),
      ),
      socialChannelsWithPostedReviews$.pipe(
        catchError(() => of(null)),
        take(1),
      ),
      widgetExposures$.pipe(
        catchError(() => of(null)),
        take(1),
      ),
      this.realtorDomainExposures$.pipe(
        catchError(() => of(null)),
        take(1),
      ),
    ]).pipe(
      map(([googleReviewInsights, externalReviewPlatform, socialChannelsWithPostedReviews, widgetExposures, realtorDomainExposures]) =>
        this.getExternalPlatformsWithReviews(
          googleReviewInsights,
          externalReviewPlatform,
          socialChannelsWithPostedReviews,
          widgetExposures,
          realtorDomainExposures ?? undefined,
        ),
      ),
    );
  }

  private getExternalReviewPlatform(): Observable<ExternalReviewSourceType | null> {
    const hasDomain$ = this.subscriptionFeatureService.features$.pipe(map((features) => features.HasDomainDisplayReviews));
    const hasRealtor$ = this.subscriptionFeatureService.features$.pipe(map((features) => features.HasRealtorDisplayReivewsInstant));

    return combineLatest([hasDomain$, hasRealtor$]).pipe(
      map(([hasDomain, hasRealtor]) => {
        if (hasDomain) {
          return 'Domain' as const;
        }
        if (hasRealtor) {
          return 'Realtor' as const;
        }
        return null;
      }),
      rmaShareReplay(),
    );
  }

  private getSocialChannelsWithReviews(from: Date, to: Date): Observable<SocialMediaChannel2[]> {
    return this.socialAnalyticsApiService.getMyActivityCountByType(from, to).pipe(
      map((stats) => {
        const channels = Object.keys(SocialMediaChannel2);

        const channelsWithReviewActivities = channels
          // SAFETY(legacy): unreviewed, pre-dates this rule.
          .map((ch) => ch as SocialMediaChannel2)
          .filter((ch) => {
            return stats[ch] && (stats[ch].review > 0 || stats[ch].externalReview > 0);
          });

        return channelsWithReviewActivities;
      }),
      rmaShareReplay(),
    );
  }

  private getIntegrationInsights(externalReviewSource: ExternalReviewSourceType, from: Date, to: Date): Observable<PlatformReviewInfo> {
    return combineLatest([
      this.getIntegrationTrixels(from, to, externalReviewSource),
      this.contextReviewsConversionsService
        .getConvertedReviewCountByReviewProfile(externalReviewSource, from)
        .pipe(catchAndLogError<number>(of(0))),
    ]).pipe(
      map(([trixels, conversion]) => ({
        totalExposures: trixels ?? 0,
        convertedReviews: conversion,
      })),
      nonNullable(),
      rmaShareReplay(),
    );
  }

  private getIntegrationTrixels(from: Date, to: Date, sourceCategoryOne?: string, sourceCategoryTwo?: string): Observable<number> {
    return combineLatest([
      this.profileContextService.isAgent$,
      this.trixelsService
        .getMetrics({
          to,
          from,
          sourceCategoryOne: sourceCategoryOne,
          sourceCategoryTwo: sourceCategoryTwo,
        })
        .pipe(catchAndLogError<Partial<TrixelsMetrics>>(of({ totalAgentExposures: 0, totalAgencyExposures: 0 }))),
    ]).pipe(
      map(([isAgent, trixelData]) => (isAgent ? (trixelData.totalAgentExposures ?? 0) : (trixelData.totalAgencyExposures ?? 0))),
      nonNullable(),
      rmaShareReplay(),
    );
  }

  private getExternalPlatformsWithReviews(
    googleReviewInsights: ConvertedReviewInsights | null,
    externalReviewSource: ExternalReviewSourceType | null,
    socialPlatforms: SocialMediaChannel2[] | null,
    widgetExposures: number | null,
    realtorDomainExposures?: PlatformReviewInfo,
  ): SyndicationChannel[] {
    const platformConfig = [
      {
        label: 'google' as const,
        show: !!googleReviewInsights?.convertedFromRmaReviewsCount,
      },
      {
        label: 'domain' as const,
        show: externalReviewSource === 'Domain' && realtorDomainExposures && realtorDomainExposures.convertedReviews > 0,
      },
      {
        label: 'realtor' as const,
        show: externalReviewSource === 'Realtor' && realtorDomainExposures && realtorDomainExposures.convertedReviews > 0,
      },
      {
        label: 'instagram' as const,
        show: socialPlatforms?.includes(SocialMediaChannel2.instagram),
      },
      {
        label: 'facebook' as const,
        show: socialPlatforms?.includes(SocialMediaChannel2.facebook),
      },
      {
        label: 'twitter' as const,
        show: socialPlatforms?.includes(SocialMediaChannel2.twitter),
      },
      {
        label: 'linkedIn' as const,
        show: socialPlatforms?.includes(SocialMediaChannel2.linkedIn),
      },
      {
        label: 'widget' as const,
        show: widgetExposures && widgetExposures > 0,
      },
    ];

    return platformConfig.filter((x) => x.show).map((x) => x.label);
  }
}
